/**
 * One status vocabulary for both inbound surfaces — the email Activity list and
 * the file Runs console. A status must not mean two different things on two
 * screens, so the badge classes, the labels and the filter list live here rather
 * than in either component.
 */

import type {
  ImportStats,
  InboundEmailListItem,
  InboundEmailStatus,
  RunExclusion,
} from '../../services/integrations-api';
import { formatPlain } from '../../utils/temporal';

export const STATUS_FILTERS: { value: '' | InboundEmailStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'awaiting_map', label: 'Awaiting approval' },
  { value: 'quarantined', label: 'Held (format changed)' },
  { value: 'failed', label: 'Failed' },
  { value: 'discarded', label: 'Discarded' },
  { value: 'rejected_sender', label: 'Rejected sender' },
  { value: 'processing', label: 'Processing' },
  { value: 'received', label: 'Queued' },
  { value: 'unmatched', label: 'Unmatched' },
];

/** Tinted badge. `alert` is the state colour; `destructive` is reserved for actions. */
export function statusBadgeClass(status: InboundEmailStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success/15 text-success';
    case 'failed':
      return 'bg-alert/15 text-alert';
    case 'quarantined':
    case 'awaiting_map':
      return 'bg-warn/15 text-warn';
    case 'rejected_sender':
    case 'unmatched':
      return 'bg-info/15 text-info';
    case 'processing':
    case 'received':
      return 'bg-accent text-primary';
    default: // discarded, needs_review
      return 'bg-muted text-muted-foreground';
  }
}

/** Solid dot for the run rail — same colour family as the badge. */
export function statusDotClass(status: InboundEmailStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success';
    case 'failed':
      return 'bg-alert';
    case 'quarantined':
    case 'awaiting_map':
      return 'bg-warn';
    case 'rejected_sender':
    case 'unmatched':
      return 'bg-info';
    case 'processing':
    case 'received':
      return 'bg-primary';
    default:
      return 'bg-muted-foreground';
  }
}

export function statusText(status: InboundEmailStatus): string {
  return status.replace(/_/g, ' ');
}

/**
 * Pipeline reject reasons are written for the log, not for a person. Translate
 * the ones a user can actually see; pass anything else through unchanged rather
 * than hiding it.
 */
export function rejectReasonText(reason: string | null): string | null {
  if (!reason) return null;
  if (reason.startsWith('unparseable_csv')) return "couldn't read the file";
  if (reason.startsWith('shape_drift')) {
    const detail = reason.slice('shape_drift'.length).trim().replace(/^\((.*)\)$/, '$1');
    return detail || 'format changed';
  }
  switch (reason) {
    case 'no_csv_attachment':
      return 'no CSV attached';
    case 'classified_dimension':
      return 'reference table, not fact data';
    case 'classified_reject':
      return "doesn't belong to this feed";
    case 'map_pending_approval':
      return 'waiting for map approval';
    default:
      return reason;
  }
}

/** Full timestamp, in the viewer's zone. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Short day label for the run rail ("Jul 27"). */
export function formatRunDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** The file a run carried. Falls back to the subject, then to "not stored". */
export function runTitle(run: InboundEmailListItem): string {
  return run.fileName || run.subject || '(not stored)';
}

/** The one-line outcome under a run's name in the rail. */
export function runOutcomeLine(run: InboundEmailListItem): string {
  const stats = run.importStats;
  if (run.status === 'completed' && stats) {
    const excluded = stats.rejectedRows + stats.failedRows;
    return `${stats.writtenRows.toLocaleString()} written · ${
      excluded === 0 ? 'none excluded' : `${excluded.toLocaleString()} excluded`
    }`;
  }
  const why = rejectReasonText(run.rejectReason);
  switch (run.status) {
    case 'completed':
      return 'imported';
    case 'quarantined':
      return `held — ${why ?? 'format changed'}`;
    case 'awaiting_map':
      return 'waiting for map approval';
    case 'discarded':
      return `not imported${why ? ` — ${why}` : ''}`;
    case 'failed':
      return `failed${run.errorMessage ? ` — ${run.errorMessage}` : ''}`;
    case 'rejected_sender':
      return `sender blocked — ${run.sender}`;
    case 'unmatched':
      return 'no matching feed';
    case 'received':
    case 'processing':
      return 'in progress';
    default:
      return statusText(run.status);
  }
}

// ── Run arithmetic (shared with the console's tests) ─────────────────────────

export type RunFilter = 'all' | 'excluded' | 'attention';

export const excludedCount = (s: ImportStats | null | undefined): number =>
  s ? s.rejectedRows + s.failedRows : 0;

export function matchesFilter(run: InboundEmailListItem, filter: RunFilter): boolean {
  if (filter === 'excluded') return excludedCount(run.importStats) > 0;
  if (filter === 'attention') return run.status === 'quarantined' || run.status === 'failed';
  return true;
}

const DAY_MS = 86_400_000;

/** Inclusive day count between two plain calendar dates, read as UTC to defeat conversion. */
export function plainSpanDays(minDate: string, maxDate: string): number | null {
  const a = Date.parse(`${minDate}T00:00:00Z`);
  const b = Date.parse(`${maxDate}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / DAY_MS) + 1;
}

/** Coverage is a plain business-local span, so it is never passed through `new Date()`. */
export function coverageText(coverage: { minDate: string; maxDate: string }): string {
  const full: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (coverage.minDate === coverage.maxDate) return formatPlain(coverage.minDate, full);
  const span = plainSpanDays(coverage.minDate, coverage.maxDate);
  const range = `${formatPlain(coverage.minDate, {
    month: 'short',
    day: 'numeric',
  })} – ${formatPlain(coverage.maxDate, full)}`;
  return span ? `${range} · ${span} day${span === 1 ? '' : 's'}` : range;
}

export interface ReasonGroup {
  reason: string;
  kind: RunExclusion['kind'];
  count: number;
}

/** Identical reasons roll up — the point of keeping reject reasons row-independent. */
export function rollUpReasons(exclusions: RunExclusion[]): ReasonGroup[] {
  const groups = new Map<string, ReasonGroup>();
  for (const e of exclusions) {
    const key = `${e.kind}${e.reason}`;
    const hit = groups.get(key);
    if (hit) hit.count++;
    else groups.set(key, { reason: e.reason, kind: e.kind, count: 1 });
  }
  return [...groups.values()].sort(
    (a, b) => b.count - a.count || a.reason.localeCompare(b.reason),
  );
}
